const isDevelopment = process.env.NODE_ENV === 'development';

export const API_BASE_URL = isDevelopment ? 'http://localhost:7777' : '';

export const FEATURE_POLICY_FIELDS = [
  {
    key: 'cli',
    label: 'CLI',
    description: 'Allow CLI and local tooling.',
  },
  {
    key: 'mcp',
    label: 'MCP',
    description: 'Allow MCP and automation integrations.',
  },
  {
    key: 'exports',
    label: 'Exports',
    description: 'Allow export and download workflows.',
  },
  {
    key: 'chat',
    label: 'Chat',
    description: 'Enable chat workflows in the app.',
  },
  {
    key: 'agent',
    label: 'Agent',
    description: 'Enable agent workflows and handoffs.',
  },
  {
    key: 'transcription',
    label: 'Transcription',
    description: 'Allow new transcription jobs.',
  },
  {
    key: 'videoGeneration',
    label: 'Video Generation',
    description: 'Allow video generation and rendering.',
  },
  {
    key: 'arrangementAudio',
    label: 'Arrangement Audio',
    description: 'Allow arrangement audio rendering.',
  },
  {
    key: 'showMp3',
    label: 'Show MP3',
    description: 'Allow show MP3 generation.',
  },
] as const;

export const ORGANIZATION_ROLE_OPTIONS = [
  { value: 'reader', label: 'Reader' },
  { value: 'writer', label: 'Writer' },
  { value: 'org_admin', label: 'Org Admin' },
  { value: 'master', label: 'Master' },
] as const;

export const SESSION_ACCESS_OPTIONS = [
  { value: 'read', label: 'Read' },
  { value: 'write', label: 'Write' },
  { value: 'manage', label: 'Manage' },
] as const;

export type FeatureKey = typeof FEATURE_POLICY_FIELDS[number]['key'];
export type FeaturePolicy = Record<FeatureKey, boolean>;
export type FeatureOverrideMode = 'inherit' | 'allow' | 'disable';
export type OrganizationRole = typeof ORGANIZATION_ROLE_OPTIONS[number]['value'];
export type SessionAccessLevel = typeof SESSION_ACCESS_OPTIONS[number]['value'];

export interface MemberUsageActivityPoint {
  date: string;
  events: number;
  totalCostUsd: number;
  totalCreditsUsed?: number;
}

export interface OrganizationMemberUsage {
  userEmail: string | null;
  days: number;
  activityDays: number;
  events: number;
  totalCostUsd: number;
  totalCreditsUsed?: number;
  lastUsageAt: string | null;
  lastSessionUsedAt: string | null;
  lastUsedAt: string | null;
  recentActivity: MemberUsageActivityPoint[];
}

export interface BillingPeriod {
  periodId: string;
  startIso: string;
  endIso: string;
}

export interface BillingRateEntry {
  provider: string;
  service: string;
  operation: string;
  creditsPerUsd: number;
}

export interface BillingPolicy {
  defaultCreditsPerUsd: number;
  defaultMonthlyCreditLimit: number;
  defaultMonthlyUsdLimit: number;
  warningThresholdPercent: number;
  serviceCreditRates: BillingRateEntry[];
}

export interface BillingOverride {
  isUnlimited: boolean;
  monthlyCreditLimit: number | null;
  monthlyUsdLimit: number | null;
}

export interface BillingSnapshot {
  period: BillingPeriod;
  isUnlimited: boolean;
  monthlyUsdLimit: number | null;
  monthlyCreditLimit: number | null;
  totalCostUsd: number;
  totalCreditsUsed: number;
  usdRemaining: number | null;
  creditsRemaining: number | null;
  percentUsdUsed: number | null;
  percentCreditsUsed: number | null;
  warningThresholdPercent: number;
  isWarning: boolean;
  isOverLimit: boolean;
  events: number;
  lastUsageAt: string | null;
  ratePolicy: {
    defaultCreditsPerUsd: number;
    serviceCreditRates: BillingRateEntry[];
  };
  override: BillingOverride | null;
  byDay?: OrganizationBillingDailyPoint[];
  byProvider?: Array<{
    provider: string;
    service: string;
    events: number;
    totalCostUsd: number;
    totalCreditsUsed: number;
  }>;
  byStatus?: Array<{
    costStatus: string;
    events: number;
    totalCostUsd: number;
    totalCreditsUsed: number;
  }>;
  byMonth?: Array<{
    periodId: string;
    events: number;
    totalCostUsd: number;
    totalCreditsUsed: number;
  }>;
}

export interface OrganizationBillingDailyPoint {
  date: string;
  events: number;
  totalCostUsd: number;
  totalCreditsUsed: number;
}

export interface OrganizationBillingOverview {
  period: BillingPeriod;
  policy: BillingPolicy;
  totals: {
    totalCostUsd: number;
    totalCreditsUsed: number;
    events: number;
    unlimitedMembers: number;
    limitedMembers: number;
    overLimitMembers: number;
    remainingCredits: number | null;
    remainingUsd: number | null;
  };
  byDay: OrganizationBillingDailyPoint[];
  byProvider: Array<{
    provider: string;
    service: string;
    events: number;
    totalCostUsd: number;
    totalCreditsUsed: number;
  }>;
  byStatus: Array<{
    costStatus: string;
    events: number;
    totalCostUsd: number;
    totalCreditsUsed: number;
  }>;
  byMonth: Array<{
    periodId: string;
    events: number;
    totalCostUsd: number;
    totalCreditsUsed: number;
  }>;
  byUser: Array<{
    userEmail: string | null;
    events: number;
    totalCostUsd: number;
    totalCreditsUsed: number;
  }>;
}

export interface AccessProfile {
  user: {
    id: string;
    email: string;
    name: string;
    picture: string | null;
    sub: string | null;
  };
  organization: {
    id: string;
    name: string;
    slug: string;
    settings: Record<string, unknown>;
    featurePolicy: FeaturePolicy;
  };
  membership: {
    role: OrganizationRole;
    capabilities: Partial<Record<FeatureKey, boolean>>;
  };
  permissions: {
    role: OrganizationRole;
    organization: {
      read: boolean;
      manage: boolean;
      viewUsage: boolean;
    };
    sessions: {
      read: boolean;
      create: boolean;
    };
    arrangements: {
      read: boolean;
      create: boolean;
    };
    features: FeaturePolicy;
  };
  cliVisibility?: {
    allowedFamilies: string[];
    deniedFamilies: string[];
    sessionScope: string | null;
  };
  billing?: BillingSnapshot;
}

export interface OrganizationMember {
  id: string;
  email: string;
  name: string;
  picture: string | null;
  role: OrganizationRole;
  capabilities: Partial<Record<FeatureKey, boolean>>;
  createdAt?: string;
  updatedAt?: string;
  lastLoginAt?: string | null;
  usage?: OrganizationMemberUsage | null;
  billing?: BillingSnapshot | null;
}

export interface UsageTotals {
  events: number;
  total_cost_usd: number;
  total_credits_used?: number;
  total_units: number;
  bytes_out: number;
}

export interface UsageProviderBreakdown {
  provider: string;
  service: string;
  events: number;
  total_cost_usd: number;
  total_credits_used?: number;
  total_units: number;
}

export interface UsageOperationBreakdown {
  provider: string;
  service: string;
  operation: string;
  events: number;
  total_cost_usd: number;
  total_credits_used?: number;
  total_quantity: number;
  unit: string | null;
}

export interface UsageStatusBreakdown {
  cost_status: string;
  events: number;
  total_cost_usd: number;
  total_credits_used?: number;
}

export interface UsageUserBreakdown {
  user_email: string | null;
  events: number;
  total_cost_usd: number;
  total_credits_used?: number;
}

export interface UsageEvent {
  id: string;
  user_email: string | null;
  provider: string;
  service: string;
  operation: string;
  resource_type: string | null;
  resource_id: string | null;
  job_id: string | null;
  model: string | null;
  total_units: number;
  quantity: number;
  unit: string | null;
  bytes_out: number;
  duration_ms: number;
  cost_usd: number;
  credits_used?: number;
  credit_rate?: number | null;
  billing_period?: string | null;
  price_source?: string | null;
  cost_status: string;
  byok: number;
  cache_hit: number;
  metadata_json: string;
  created_at: string;
}

export interface UsageSummary {
  totals: UsageTotals;
  byProvider: UsageProviderBreakdown[];
  byOperation: UsageOperationBreakdown[];
  byUser: UsageUserBreakdown[];
  byStatus: UsageStatusBreakdown[];
  days: number;
}

export interface SettingsOverviewResponse {
  accessProfile: AccessProfile;
  selfUsage: {
    summary: UsageSummary;
    recent: UsageEvent[];
    activity: OrganizationMemberUsage | null;
  };
  organization: {
    id: string;
    name: string;
    slug: string;
    settings: Record<string, unknown>;
    featurePolicy: FeaturePolicy;
    billing?: OrganizationBillingOverview;
    members: OrganizationMember[];
  };
  usage: {
    summary: UsageSummary;
    recent: UsageEvent[];
  } | null;
}

export interface SessionGrant {
  id: string;
  email: string;
  name: string;
  accessLevel: SessionAccessLevel;
  grantedBy?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface SessionAccessResponse {
  sessionId: string;
  ownerEmail: string | null;
  canManage: boolean;
  currentUserAccess: {
    role: OrganizationRole;
    canRead: boolean;
    canWrite: boolean;
    canManage: boolean;
    isOwner: boolean;
    isOrgAdmin: boolean;
    isMaster: boolean;
    grantLevel: SessionAccessLevel;
    organizationId: string;
    ownerEmail: string | null;
  };
  grants: SessionGrant[];
}

export interface RequestErrorPayload {
  error?: string;
  message?: string;
}

export async function authorizedJsonRequest<T>(
  path: string,
  token?: string | null,
  options: RequestInit = {}
): Promise<T> {
  const headers = new Headers(options.headers);

  if (options.body !== undefined && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  if (token && token !== 'cookie-session') {
    headers.set('Authorization', `Bearer ${token}`);
  }

  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...options,
    headers,
    credentials: options.credentials || 'include',
  });

  const responseText = await response.text();
  const data = responseText ? safeJsonParse(responseText) : null;

  if (!response.ok) {
    const payload = data as RequestErrorPayload | string | null;
    const message =
      (typeof payload === 'object' && payload && (payload.error || payload.message)) ||
      (typeof payload === 'string' && payload) ||
      `Request failed (${response.status})`;
    throw new Error(message);
  }

  return data as T;
}

export function featureOverrideMode(value: boolean | undefined): FeatureOverrideMode {
  if (value === true) return 'allow';
  if (value === false) return 'disable';
  return 'inherit';
}

export function capabilityPayloadFromModes(
  modes: Partial<Record<FeatureKey, FeatureOverrideMode>>
): Partial<Record<FeatureKey, boolean>> {
  return FEATURE_POLICY_FIELDS.reduce<Partial<Record<FeatureKey, boolean>>>((acc, feature) => {
    const mode = modes[feature.key];
    if (mode === 'allow') {
      acc[feature.key] = true;
    } else if (mode === 'disable') {
      acc[feature.key] = false;
    }
    return acc;
  }, {});
}

export function formatCurrency(value: number | null | undefined): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value || 0));
}

export function formatNumber(value: number | null | undefined): string {
  return new Intl.NumberFormat('en-US').format(Number(value || 0));
}

export function formatCredits(value: number | null | undefined): string {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(Number(value || 0));
}

export function formatBytes(value: number | null | undefined): string {
  const bytes = Number(value || 0);
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let size = bytes / 1024;
  let unitIndex = 0;
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex += 1;
  }
  return `${size.toFixed(size >= 100 ? 0 : 1)} ${units[unitIndex]}`;
}

export function formatDateTime(value: string | null | undefined): string {
  if (!value) return 'Unknown';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('en-US', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
}

export function formatRelativeTime(value: string | null | undefined): string {
  if (!value) return 'No recent activity';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const diffMs = Date.now() - date.getTime();
  const minute = 60 * 1000;
  const hour = 60 * minute;
  const day = 24 * hour;
  if (diffMs < minute) return 'Just now';
  if (diffMs < hour) return `${Math.max(1, Math.round(diffMs / minute))} min ago`;
  if (diffMs < day) return `${Math.max(1, Math.round(diffMs / hour))} hr ago`;
  return `${Math.max(1, Math.round(diffMs / day))} day${Math.round(diffMs / day) === 1 ? '' : 's'} ago`;
}

export function roleLabel(role: OrganizationRole): string {
  return ORGANIZATION_ROLE_OPTIONS.find((option) => option.value === role)?.label || role;
}

function safeJsonParse(value: string): unknown {
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
}
